import { useNavigate } from "react-router";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ExistingDestination } from "@prequel/react";

import DataIntegrityService from "@/api/services/data_integrity";
import { useToast } from "@/providers/ToastProvider";
import { DataIntegrityJob } from "@/types";

export function useDataIntegrityJobs(destinationId: ExistingDestination["id"] | undefined) {
  return useQuery({
    queryKey: ["data-integrity", "list", destinationId],
    queryFn: destinationId ? () => DataIntegrityService.getDataIntegrityJobs(destinationId) : skipToken,
  });
}

export function useDataIntegrityJob(
  destinationId: ExistingDestination["id"] | undefined,
  jobId: DataIntegrityJob["id"] | undefined,
) {
  return useQuery({
    queryKey: ["data-integrity", "detail", destinationId, jobId],
    queryFn:
      destinationId && jobId ? () => DataIntegrityService.getDataIntegrityJobByID(destinationId!, jobId!) : skipToken,
  });
}

export function useCreateDataIntegrityJob() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (payload: {
      destinationID: ExistingDestination["id"];
      modelNames?: string[];
      checkValueIntegrity?: boolean;
    }) => DataIntegrityService.createDataIntegrityJob(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["data-integrity", data.destinationID],
      });
      queryClient.invalidateQueries({
        queryKey: ["destinations", data.destinationID],
      });
      queryClient.invalidateQueries({
        queryKey: ["destination-activities"],
      });
      addToast({
        kind: "SUCCESS",
        message: "Data integrity check enqueued successfully!",
      });
    },
  });
}

export function useCancelDataIntegrityJob() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { jobID: string; redirectPath?: string }) =>
      DataIntegrityService.cancelDataIntegrityJob(payload.jobID),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["data-integrity"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}
